import { useEffect, useMemo, useRef, useState } from "react";
import { useSelectedMarker, useSelectedOnlineNetworks, useSetSelectedMarker } from "../../../state/application-state-service";
import { createTrackSectionFromGeoJson, GeoJsonNetworkAdapter, GeoJsonTrackPoint, GeoJsonTrackSection } from "../../../utils/onlineMapsUtils";
import { useInputParamState } from "../../../state/Input-param-state";
import { DynamicInputParameters } from "../../../types/ApiData";
import { useCoordPassSet } from "../../../hooks/useCoordPassSet";
import { useOktaAuth } from "@okta/okta-react";
import { FixedSizeList, FixedSizeList as FixedSizeListComponent } from "react-window";
import { useStoredLocationData, useStoredLocationParams, useAllTags, useSearchText, useSearchMap, useIsSearchActive, updateSearch, clearSearch, setSearchText, useParamFilterState, useStoredParameters, useResponseSchema, deletePermenantLocData, deleteAllPermenantLocData, useNetwork, updateFilter } from '../../../state/services';
import { Box, Typography, Grid, Button, TextField, IconButton } from '@mui/material';
import { Bookmark, BookmarkBorder, ExpandMore, Search } from "@mui/icons-material";
import { hasActiveFilter } from "../../../utils/filterUtils";
import { Accordion, AccordionSummary, AccordionDetails, Chip, Tooltip } from '@mui/material'
import { QueryStats, ContentCopy, RemoveCircleOutlineOutlined } from '@mui/icons-material';
import { ConfirmationAlert, DisplayDialog } from "../../home/right-screen/RightPanelTab";
import ReusableDialog from "../../../components/Dialog";
import { TagDialogDetails } from "../../home/heat-map/HeatMap";
import List from "@mui/material/List";
import { OnlineCopyDialog } from "./OnlineCopyDialog";
import { newId } from "../../home/heat-map/GlobalFilter";


export const OnlineSidePanel = () => {
  const geoJsonData = useSelectedOnlineNetworks();
  const selectedMarker = useSelectedMarker();
  const setSelectedMarker = useSetSelectedMarker();
  const selectedNetwork = useNetwork();

  const paramState = useInputParamState();
  const locationData = useStoredLocationData();

  const storedInputParams = useInputParamState();

  const allTags = useAllTags();
  const searchText = useSearchText();
  const searchMap = useSearchMap();
  const isSearchActive = useIsSearchActive();
  const paramFilter = useParamFilterState();
  const storedLocationParams = useStoredLocationParams();
  const responseSchema = useResponseSchema();

  const savedGlobalParams =
    useStoredParameters() as DynamicInputParameters[];

  const { authState } = useOktaAuth();

  const accessToken =
    authState?.accessToken?.accessToken ?? "";

  const section = useMemo(
    () =>
      createTrackSectionFromGeoJson(geoJsonData, {
        networkId: selectedNetwork.id,
        trackId: selectedNetwork.id,
        trackName:
          selectedNetwork.name || "Selected Track",
      }),
    [
      geoJsonData,
      selectedNetwork.id,
      selectedNetwork.name,
    ]
  );

  const network = useMemo<GeoJsonNetworkAdapter>(
    () => ({
      id:
        section?.networkId ||
        selectedNetwork.id ||
        "selected-online-network",
      edgesOfNetwork: section ? [section] : [],
    }),
    [section, selectedNetwork.id]
  );

  const hasNetwork =
    Boolean(section) && section!.points.length > 0;

  const { coordPassSet } = useCoordPassSet({
    network,
    savedGlobalParams,
    storedLocationParams,
    paramFilter,
    networkId: network.id,
  });

  const [isExpanded, setIsExpanded] = useState(true);

  const accordionRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<any>(null);

  const rowHeight = 40;
  const maxVisibleRows = 10;

  const [formOpen, setFormOpen] =
    useState(false);

  const [
    selectedCoordResult,
    setSelectedCoordResult,
  ] = useState<
    Array<{
      key: string;
      label: string;
      unit?: string;
      type?: number;
      options?: Array<{
        label: string;
        value: string;
      }>;
      displayValue: unknown;
    }>
  >([]);

  const [copy, setCopy] = useState(false);

  const [copyParamCoord, setCopyParamCoord] =
    useState<{
      source_coord: string;
      source_name: string;
      id: string
    }>({
      source_coord: "",
      source_name: "",
      id: ''
    });

  const [tagsPopupOpen, setTagsPopupOpen] =
    useState(false);

  const [sectionDetails, setSectionDetails] =
    useState<{
      edgeId?: string;
      edgeName?: string;
    }>({});

  const [deleteCalc, setDeleteCalc] =
    useState<{
      isDelete: boolean;
      locParams: any;
      network_id: string;
      section_id: string;
    }>({
      isDelete: false,
      locParams: "",
      network_id: "",
      section_id: "",
    });

  const [deleteAll, setDeleteAll] =
    useState(false);

  /*
   * Filter the points once.
   *
   * This same array is used for:
   * - rendering
   * - selected-marker lookup
   * - automatic list scrolling
   */
  const filteredPoints = useMemo(() => {
    if (!section) {
      return [];
    }

    return section.points
      .map((point, originalIndex) => ({
        point,
        originalIndex,
      }))
      .filter(({ point }) => {
        const [longitude, latitude] =
          point.coordinates;

        if (isSearchActive) {
          const edgeSearch =
            searchMap[section.railwayEdgeId];

          if (!edgeSearch) {
            return false;
          }

          const coordinateKey = `${latitude}-${longitude}`;

          if (
            !edgeSearch.edgeMatch &&
            !edgeSearch.coordMatches.has(
              coordinateKey
            )
          ) {
            return false;
          }
        }

        if (
          hasActiveFilter(paramFilter) &&
          coordPassSet &&
          !coordPassSet.has(
            `${latitude},${longitude}`
          )
        ) {
          return false;
        }

        return true;
      });
  }, [
    section,
    isSearchActive,
    searchMap,
    paramFilter,
    coordPassSet,
  ]);

  useEffect(() => {
    if (!selectedMarker || filteredPoints.length === 0) {
      return;
    }

    setIsExpanded(true);

    const selectedIndex = filteredPoints.findIndex(
      ({ point }) => point.featureId === selectedMarker
    );

    if (selectedIndex < 0) {
      return;
    }

    const scrollToSelectedItem = () => {
      if (!listRef.current) {
        return;
      }

      /*
       * Use react-window's API, not the DOM scrollbar.
       * This works when moving both upward and downward.
       */
      listRef.current.scrollTo(
        selectedIndex * rowHeight
      );

      accordionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    };

    /*
     * Give the accordion time to open first.
     */
    const timeoutId = window.setTimeout(() => {
      window.requestAnimationFrame(
        scrollToSelectedItem
      );
    }, isExpanded ? 0 : 300);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [
    selectedMarker,
    filteredPoints,
    isExpanded,
  ]);
  /*
   * Clear online selection when a different network
   * is selected.
   */
  useEffect(() => {
    clearSearch(paramState);
    setSelectedMarker("");
    setIsExpanded(true);
    updateFilter(paramState, { logic: "AND", groups: [ { id: newId(), logic: "AND", rows: [] }]})
  }, [selectedNetwork.id]);

  const handleAccordionChange = (
    _event: React.SyntheticEvent,
    expanded: boolean
  ) => {
    setIsExpanded(expanded);
    if (!expanded) {
      setSelectedMarker("");
    }
  };

  const handleCoordinateClick = (
    point: GeoJsonTrackPoint
  ) => {

    if (
      selectedMarker === point.featureId
    ) {
      setSelectedMarker("");
      return;
    }

    setIsExpanded(true);
    setSelectedMarker(point.featureId);
  };

  const handleTagDialogOpen = (
    id: string,
    name: string
  ) => {
    setSectionDetails({
      edgeId: id,
      edgeName: name,
    });

    setTagsPopupOpen(true);
  };

  const handleSearch = () => {
    const normalizedSearch =
      searchText.trim().toLowerCase();

    if (!normalizedSearch) {
      updateSearch(paramState, {}, false);
      return;
    }

    const resultMap: Record<
      string,
      {
        edgeMatch: boolean;
        coordMatches: Set<string>;
      }
    > = {};

    Object.values(allTags).forEach(
      (point) => {
        const hasMatch = point.tags.some(
          (tag) =>
            tag
              .toLowerCase()
              .includes(normalizedSearch)
        );

        if (!hasMatch) {
          return;
        }

        if (!resultMap[point.edgeId]) {
          resultMap[point.edgeId] = {
            edgeMatch: false,
            coordMatches: new Set(),
          };
        }

        if (
          point.lat === 0 &&
          point.lng === 0
        ) {
          resultMap[
            point.edgeId
          ].edgeMatch = true;
        } else {
          resultMap[
            point.edgeId
          ].coordMatches.add(
            `${point.lat}-${point.lng}`
          );
        }
      }
    );

    updateSearch(
      paramState,
      resultMap,
      true
    );
  };

  const handleClearSearch = () => {
    clearSearch(paramState);
    setSelectedMarker("");
  };

  const handleOpenCopy = (
    coordinate: string,
    sourceName: string,
    id: string
  ) => {
    setCopyParamCoord({
      source_coord: coordinate,
      source_name: sourceName,
      id: id
    });

    setCopy(true);
  };

  const handleCloseCopy = () => {
    setCopy(false);

    setCopyParamCoord({
      source_coord: "",
      source_name: "",
      id: ""
    });
  };

  const displayResponse = (
    value: Record<string, unknown>
  ) => {
    const schemaMap = new Map(
      responseSchema.map((schema) => [
        schema.code,
        schema,
      ])
    );

    const result = Object.entries(value)
      .filter(([key]) =>
        schemaMap.has(key)
      )
      .map(([key, itemValue]) => {
        const schema =
          schemaMap.get(key)!;

        return {
          key,
          code: schema.code,
          label: schema.label,
          hint: schema.hint,
          unit: schema.unit,
          value: itemValue,
          displayValue: itemValue,
        };
      });

    setSelectedCoordResult(result);
    setFormOpen(true);
  };

  const handleDeleteCalculations = () => {
    deletePermenantLocData(
      paramState,
      accessToken,
      deleteCalc.locParams,
      deleteCalc.network_id,
      deleteCalc.section_id
    );

    setDeleteCalc({
      isDelete: false,
      locParams: "",
      network_id: "",
      section_id: "",
    });
  };
  const deleteAllCalculations = () => {
    const networkName = network.edgesOfNetwork[0].edgeName

    deleteAllPermenantLocData(
      paramState,
      networkName,
      accessToken
    );

    setDeleteAll(false);
  };

  const hasNoResults =
    isSearchActive &&
    filteredPoints.length === 0;

  const networkName = network?.edgesOfNetwork[0]?.edgeName ?? 'unknown-network'

  const hasLocationCalc =
    Boolean(locationData && networkName) &&
    Object.values(locationData).some(
      (value) =>
        value?.network_id === networkName
    );

  const sectionTagId = section
    ? `${section.railwayEdgeId}:0-0`
    : "";

  const sectionTags = sectionTagId
    ? allTags[sectionTagId]?.tags ?? []
    : [];

  if (!hasNetwork || !section) {
    return null;
  }

  return (
    <List>
      <Box
        sx={{
          display: "flex",
          flexDirection: {
            xs: "column",
            sm: "row",
          },
          gap: 1,
          mb: 2,
        }}
      >
        <TextField
          fullWidth
          size="small"
          placeholder="Search tag..."
          value={searchText}
          onChange={(event) =>
            setSearchText(
              paramState,
              event.target.value
            )
          }
          onKeyDown={(event) => {
            if (
              event.key === "Enter" &&
              searchText.trim()
            ) {
              handleSearch();
            }
          }}
        />

        <Button
          disabled={!searchText.trim()}
          variant="outlined"
          endIcon={<Search />}
          sx={{
            textTransform: "none",
          }}
          size="small"
          onClick={handleSearch}
        >
          Search
        </Button>

        <Button
          sx={{
            textTransform: "none",
          }}
          variant="outlined"
          disabled={!searchText.trim()}
          color="error"
          size="small"
          onClick={handleClearSearch}
        >
          Clear
        </Button>
      </Box>

      {hasNoResults && (
        <Typography
          color="error"
          sx={{ mb: 2 }}
        >
          No tags found.
        </Typography>
      )}

      {hasLocationCalc && (
        <Box sx={{ p: 1 }}>
          <Button
            sx={{
              textTransform: "none",
            }}
            onClick={() =>
              setDeleteAll(true)
            }
            color="error"
            variant="outlined"
            endIcon={
              <RemoveCircleOutlineOutlined />
            }
          >
            Reset Calculations
          </Button>
        </Box>
      )}

      {!hasNoResults && (
        <Accordion
          ref={accordionRef}
          key={section.railwayEdgeId}
          disableGutters
          expanded={isExpanded}
          onChange={handleAccordionChange}
        >
          <AccordionSummary
            expandIcon={<ExpandMore />}
            sx={{
              bgcolor: isExpanded
                ? "#33ae3319"
                : "#fdfdfd",
              borderBottom: isExpanded
                ? "1px solid #33ae33"
                : "1px solid #eeeeee",
              ":hover": {
                bgcolor: "#33ae3319",
                borderBottom:
                  "1px solid #33ae33",
              },
            }}
          >
            <Grid
              container
              spacing={1}
            >
              <Grid item xs={8}>
                <Typography variant="caption">
                  Track Section
                </Typography>

                <Grid
                  container
                  alignItems="center"
                  gap={1.5}
                >
                  <Typography
                    component="span"
                    sx={{ color: "green" }}
                  >
                    {section.railwayEdgeName}{" "}
                    (
                    {Math.round(
                      section.length
                    )}
                    m)
                  </Typography>

                  <Tooltip
                    title={
                      sectionTags.length > 0
                        ? `${sectionTags[0].substring(
                          0,
                          6
                        )}...`
                        : "Add Tag"
                    }
                    placement="top"
                  >
                    {sectionTags.length > 0 ? (
                      <Bookmark
                        onClick={(event) => {
                          event.stopPropagation();

                          handleTagDialogOpen(
                            section.railwayEdgeId,
                            section.edgeName
                          );
                        }}
                        fontSize="small"
                        sx={{
                          cursor: "pointer",
                          color: "purple",
                        }}
                      />
                    ) : (
                      <BookmarkBorder
                        onClick={(event) => {
                          event.stopPropagation();

                          handleTagDialogOpen(
                            section.railwayEdgeId,
                            section.edgeName
                          );
                        }}
                        fontSize="small"
                        sx={{
                          cursor: "pointer",
                          color: "black",
                        }}
                      />
                    )}
                  </Tooltip>
                </Grid>
              </Grid>
            </Grid>
          </AccordionSummary>

          <AccordionDetails>
            {isExpanded &&
              filteredPoints.length > 0 && (
                <FixedSizeList
                  ref={listRef}
                  height={Math.min(
                    filteredPoints.length * rowHeight,
                    maxVisibleRows * rowHeight
                  )}
                  itemCount={
                    filteredPoints.length
                  }
                  itemSize={rowHeight}
                  width="100%"
                  overscanCount={filteredPoints.length}
                >
                  {({
                    index,
                    style,
                  }: {
                    index: number;
                    style: React.CSSProperties;
                  }) => {
                    const {
                      point,
                      originalIndex,
                    } =
                      filteredPoints[index];

                    const [
                      longitude,
                      latitude,
                    ] = point.coordinates;

                    const isSelected =
                      selectedMarker ===
                      point.featureId;

                    const pointId = `${section.railwayEdgeId}:${latitude}-${longitude}`;

                    const edgePointId = `${section.railwayEdgeId}:0-0`;

                    const tags =
                      allTags[pointId]
                        ?.tags ??
                      allTags[edgePointId]
                        ?.tags ??
                      [];

                    const locationKey = `${latitude},${longitude}`;

                    const locationResult =
                      locationData[
                      locationKey
                      ];

                    const hasLocationData =
                      typeof locationResult ===
                      "object" &&
                      locationResult !== null &&
                      Object.keys(
                        locationResult
                      ).length > 0;

                    const pointName =
                      point.name ||
                      `Node ${originalIndex + 1
                      }`;

                    const sectionId = `${pointName}`;

                    return (
                      <div
                        key={point.featureId}
                        style={{
                          ...style,
                          width: "100%",
                          boxSizing:
                            "border-box",
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            px: 1,
                            py: 0.5,
                            height: "100%",
                            display: "flex",
                            alignItems:
                              "center",
                          }}
                        >
                          <Grid
                            container
                            alignItems="center"
                            wrap="nowrap"
                            sx={{
                              minWidth: 0,
                              width: "100%",
                            }}
                          >
                            <Grid
                              item
                              sx={{
                                flex: "1 1 0",
                                minWidth: 0,
                                overflow:
                                  "hidden",
                              }}
                            >
                              <Chip
                                label={
                                  <Box
                                    sx={{
                                      display:
                                        "flex",
                                      alignItems:
                                        "center",
                                      gap: {
                                        xs: 0.25,
                                        sm: 0.5,
                                      },
                                      minWidth: 0,
                                      maxWidth:
                                        "100%",
                                    }}
                                  >
                                    <Box
                                      component="span"
                                      sx={{
                                        minWidth: 0,
                                        overflow:
                                          "hidden",
                                        textOverflow:
                                          "ellipsis",
                                        whiteSpace:
                                          "nowrap",
                                        display:
                                          "block",
                                      }}
                                    >
                                      {pointName}
                                    </Box>

                                    {tags.length >
                                      0 && (
                                        <Tooltip
                                          title={`${tags[0].substring(
                                            0,
                                            6
                                          )}...`}
                                          placement="top"
                                        >
                                          <Bookmark
                                            sx={{
                                              color:
                                                "purple",
                                              fontSize:
                                              {
                                                xs: 16,
                                                sm: 18,
                                              },
                                              ml: 0.25,
                                            }}
                                          />
                                        </Tooltip>
                                      )}
                                  </Box>
                                }
                                onClick={() =>
                                  handleCoordinateClick(
                                    point
                                  )
                                }
                                sx={{
                                  maxWidth:
                                    "100%",
                                  color: "green",
                                  cursor:
                                    "pointer",
                                  backgroundColor:
                                    isSelected
                                      ? "#33ae3319"
                                      : "#fdfdfd",
                                  ":hover": {
                                    backgroundColor:
                                      "#33ae3319",
                                  },
                                  "& .MuiChip-label":
                                  {
                                    display:
                                      "flex",
                                    alignItems:
                                      "center",
                                    overflow:
                                      "hidden",
                                    px: {
                                      xs: 0.5,
                                      sm: 1,
                                    },
                                    py: 0,
                                    maxWidth:
                                      "100%",
                                  },
                                }}
                              />
                            </Grid>

                            {hasLocationData && (
                              <Grid
                                item
                                sx={{
                                  flex:
                                    "0 0 auto",
                                  ml: 0.25,
                                }}
                              >
                                <Box
                                  sx={{
                                    display:
                                      "flex",
                                    alignItems:
                                      "center",
                                    gap: 0.5,
                                  }}
                                >
                                  <Tooltip
                                    title="See Calculations"
                                    placement="top"
                                  >
                                    <Button
                                      variant={
                                        isSelected
                                          ? "outlined"
                                          : "contained"
                                      }
                                      onClick={(
                                        event
                                      ) => {
                                        event.stopPropagation();

                                        displayResponse(
                                          locationResult
                                        );
                                      }}
                                      sx={{
                                        color:
                                          "black",
                                        backgroundColor:
                                          isSelected
                                            ? "#33ae3319"
                                            : "white",
                                      }}
                                    >
                                      <QueryStats fontSize="small" />
                                    </Button>
                                  </Tooltip>

                                  <Tooltip
                                    title="Copy Parameters"
                                    placement="top"
                                  >
                                    <IconButton
                                      size="small"
                                      onClick={(
                                        event
                                      ) => {
                                        event.stopPropagation();

                                        handleOpenCopy(
                                          locationKey,
                                          sectionId,
                                          point.featureId
                                        );
                                      }}
                                      sx={{
                                        width: 32,
                                        height: 32,
                                        color:
                                          isSelected
                                            ? "green"
                                            : "black",
                                        p: 0,
                                        "& .MuiSvgIcon-root":
                                        {
                                          fontSize: 18,
                                        },
                                      }}
                                    >
                                      <ContentCopy fontSize="small" />
                                    </IconButton>
                                  </Tooltip>

                                  <Tooltip
                                    title="Reset Calculation"
                                    placement="top"
                                  >
                                    <IconButton
                                      size="small"
                                      onClick={(
                                        event
                                      ) => {
                                        event.stopPropagation();

                                        setDeleteCalc(
                                          {
                                            isDelete:
                                              true,
                                            locParams:
                                              locationKey,
                                            network_id:
                                              networkName,
                                            section_id:
                                              point.featureId,
                                          }
                                        );
                                      }}
                                      sx={{
                                        width: 32,
                                        height: 32,
                                        p: 0,
                                        "& .MuiSvgIcon-root":
                                        {
                                          fontSize: 18,
                                        },
                                      }}
                                    >
                                      <RemoveCircleOutlineOutlined
                                        color="error"
                                        fontSize="small"
                                      />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </Grid>
                            )}
                          </Grid>
                        </Box>
                      </div>
                    );
                  }}
                </FixedSizeList>
              )}
          </AccordionDetails>
        </Accordion>
      )}

      {Object.keys(storedInputParams)
        .length > 0 &&
        formOpen && (
          <ReusableDialog
            open={formOpen}
            cancelText="Close"
            title="Trackstability calculation Result: Section"
            content={
              <DisplayDialog
                data={selectedCoordResult}
              />
            }
            onCancel={() =>
              setFormOpen(false)
            }
          />
        )}

      {copy && (
        <OnlineCopyDialog
          accessToken={accessToken}
          copyParam={copyParamCoord}
          open={copy}
          handleClose={handleCloseCopy}
        />
      )}

      {tagsPopupOpen && (
        <ReusableDialog
          open={tagsPopupOpen}
          title="Tag Details"
          cancelText="Close"
          content={
            <TagDialogDetails
              edgeId={
                sectionDetails.edgeId
              }
              edgeName={
                sectionDetails.edgeName
              }
              isGeoPoint={false}
              section={{}}
            />
          }
          onCancel={() =>
            setTagsPopupOpen(false)
          }
        />
      )}

      {deleteCalc.isDelete && (
        <ConfirmationAlert
          open={deleteCalc.isDelete}
          handleClose={() =>
            setDeleteCalc({
              isDelete: false,
              locParams: "",
              network_id: "",
              section_id: "",
            })
          }
          handleSubmit={
            handleDeleteCalculations
          }
          message="This action will clear this geo-point configuration calculations and reset with the global settings. This cannot be undone."
          title="Are you sure you want to reset?"
        />
      )}

      {deleteAll && (
        <ConfirmationAlert
          open={deleteAll}
          handleClose={() =>
            setDeleteAll(false)
          }
          handleSubmit={
            deleteAllCalculations
          }
          message="This action will clear all geo-point configuration calculations and reset with the global settings. This cannot be undone."
          title="Are you sure you want to reset?"
        />
      )}
    </List>
  );
};